import { FCX } from "@datassential/platform-ui-lib";
import { useFoodProfileDocuments } from "@apiHooks/useFoodProfileDocuments";
import useFoodProfilePage from "@pages/Food/hooks/useFoodProfilePage";
import FoodConsumerSection from "@pages/Food/refresh/components/FoodConsumerSection/FoodConsumerSection";
import FoodLaunchesSection from "@pages/Food/refresh/components/FoodLaunchesSection/FoodLaunchesSection";
import FoodReportsSection from "@pages/Food/refresh/components/FoodReportsSection/FoodReportsSection";
import FoodSocialSection from "@pages/Food/refresh/components/FoodSocialSection/FoodSocialSection";

type Props = Pick<
    ReturnType<typeof useFoodProfilePage>["sections"],
    "summary" | "consumer" | "setHasSocial" | "setHasReports" | "setHasLaunches"
>;

/**
 * The refreshed page's sections after Menu: Reports, Consumer, Social, Launches.
 * There is no Charts section: instant charts and MACs are shown in the sidebar instead.
 *
 * The documents request for Reports is made here. Render this INSIDE `FoodProfilePageFrame`
 * (below its `!isLoading && children` gate) so that request restarts for every word: `useData` resets
 * its data only in an effect, so a hook placed above the gate would show the previous word's reports
 * for one render after `?q=` changes.
 */
const FoodRefreshSections: FCX<Props> = ({
    summary,
    consumer,
    setHasSocial,
    setHasReports,
    setHasLaunches,
}) => {
    const { data: { reports }, isLoading } = useFoodProfileDocuments(summary.name);

    return (
        <>
            <FoodReportsSection
                word={summary.name}
                data={reports}
                isLoading={isLoading}
                setHasData={setHasReports}
            />
            <FoodConsumerSection word={summary.name} data={consumer}/>
            {!!summary.id && (
                <FoodSocialSection
                    setHasData={setHasSocial}
                    word={summary.name}
                    wordId={summary.id}
                />
            )}
            <FoodLaunchesSection
                word={summary.name}
                setHasData={setHasLaunches}
            />
        </>
    );
};

export default FoodRefreshSections;
